import React, { useEffect, useRef } from 'react';
import * as Sentry from '@sentry/browser';
import { Provider } from 'react-redux';
import { listenToHeightChanges } from 'wix-height-updater';
import { win } from '@wix/cashier-common/dist/src/utils/win';
import { DeviceType } from '@wix/cashier-common/dist/src/enums/DeviceType';
import { ViewMode } from '@wix/cashier-common/dist/src/types/ViewMode';
import { useIsFirstRender } from '../../hooks/useFirstRender';
import { useOnBeforeFirstRender } from '../../hooks/useOnBeforeFirstRender';
import { defaultUserLang } from '../../utils/defaultUserLang';
import { configureStore, Store } from '../../store';
import { globals } from '../../utils/globals';
import { addI18nResources, initI18n } from '../../utils/i18n';
import { wixSdkAdapter } from '../../utils/wixSdkAdapter';
import { AppControllerConnect } from './controller';

export interface IAppIndexProps {
  locale?: string;
  staticsBaseUrl?: string;
  deviceType?: DeviceType;
  viewMode?: ViewMode;
}

const defaultProps: IAppIndexProps = {
  viewMode: 'site',
  locale: defaultUserLang,
  deviceType: DeviceType.Desktop,
};

export const WalletApp = (passedProps: IAppIndexProps) => {
  const props: IAppIndexProps = {
    ...defaultProps,
    ...passedProps,
  };
  const storeRef = useRef<Store | null>(null);
  const isFirstRender = useIsFirstRender();

  useOnBeforeFirstRender(() => {
    storeRef.current = configureStore({
      appState: {
        viewMode: props.viewMode,
        locale: props.locale,
        staticsBaseUrl: props.staticsBaseUrl,
        deviceType: props.deviceType,
        freeText: {},
      },
    });

    initI18n(globals.l10n, props.locale);

    Sentry.configureScope((scope) => {
      scope.setTag('msid', wixSdkAdapter.getMetasiteId());
    });

    listenToHeightChanges(wixSdkAdapter.Wix, win);
  });

  useEffect(() => {
    // note that locale is dynamically changed only in Storybook
    if (process.env.NODE_ENV === 'production' && !isFirstRender) {
      addI18nResources(globals.l10n, props.locale);
    }
  }, [props.locale]);

  return (
    <Provider store={storeRef.current}>
      <AppControllerConnect deviceType={props.deviceType} locale={props.locale} />
    </Provider>
  );
};
